import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { cx } from '@vizit/theme';

interface HeroSparkBetaBannerProps {
  /**
   * Banner title (e.g. "Hero Spark Images is in beta", "Mobile Readiness is in beta")
   */
  title: string;
  /**
   * Banner subtitle
   * @default "Results are directional. This feature is actively evolving."
   */
  subtitle?: string;
  /**
   * Optional class name for the root element
   */
  className?: string;
}

function HeroSparkIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.8917 1.99902L10.4095 5.2306L7.15483 6.70187L10.4095 8.17289L11.8917 11.4044L13.3737 8.17289L16.6285 6.70187L13.3737 5.2306L11.8917 1.99902Z"
        stroke="url(#hero-spark-banner-paint0)"
        strokeWidth="1.66667"
      />
      <path
        d="M3.20749 1.99902L2.83693 2.80692L2.02325 3.17474L2.83693 3.54248L3.20749 4.35037L3.57798 3.54248L4.39167 3.17474L3.57798 2.80692L3.20749 1.99902Z"
        stroke="url(#hero-spark-banner-paint1)"
        strokeWidth="1.66667"
      />
      <path
        d="M4.78648 10.2288L3.60219 12.1882L1.62851 13.364L3.60219 14.5396L4.78648 16.499L5.97061 14.5396L7.9443 13.364L5.97061 12.1882L4.78648 10.2288Z"
        stroke="url(#hero-spark-banner-paint2)"
        strokeWidth="1.66667"
      />
      <defs>
        <linearGradient
          id="hero-spark-banner-paint0"
          x1="1.62828"
          y1="16.4989"
          x2="16.12"
          y2="1.50746"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#7B6AE0" />
          <stop offset="1" stopColor="#FFBB89" />
        </linearGradient>
        <linearGradient
          id="hero-spark-banner-paint1"
          x1="1.62828"
          y1="16.4989"
          x2="16.12"
          y2="1.50746"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#7B6AE0" />
          <stop offset="1" stopColor="#FFBB89" />
        </linearGradient>
        <linearGradient
          id="hero-spark-banner-paint2"
          x1="1.62828"
          y1="16.4989"
          x2="16.12"
          y2="1.50746"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#7B6AE0" />
          <stop offset="1" stopColor="#FFBB89" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/**
 * Beta banner shown above hero spark beta features (Spark Images, Spark Ideas).
 * Uses the shared gradient-bordered styling so the purple→orange stops match
 * the design system; inner div paints white over the rest.
 */
function HeroSparkBetaBanner(props: HeroSparkBetaBannerProps) {
  const { title, subtitle = 'Results are directional. This feature is actively evolving.', className } = props;

  return (
    <div className={cx('bg-gradient-linear-12-border p-1 mb-10 shadow-xs', className)}>
      <div className="bg-primary flex items-center gap-7 p-7">
        <div className="flex items-center justify-center w-18 h-18 p-3 rounded-xxs border border-secondary bg-primary shrink-0 shadow-xs">
          <HeroSparkIcon />
        </div>
        <div className="flex flex-col gap-1">
          <Text size={TextSize.Sm} weight={TextWeight.Semibold}>
            {title}
          </Text>
          <Text size={TextSize.Xs} color={TextColor.Tertiary}>
            {subtitle}
          </Text>
        </div>
      </div>
    </div>
  );
}

export { HeroSparkBetaBanner };
export type { HeroSparkBetaBannerProps };
